/**
 * Client side of the `waadoo-credentials` backend plugin, which the access
 * card of the per-branch page reads (`CredentialsCard.tsx`).
 *
 * The types below are re-declared rather than imported from the backend, for
 * the reason `pipelines.ts` and `backups.ts` state: the frontend bundle must
 * not pull a backend package in, and a shared package for one interface would
 * cost more than the duplication. They are the contract of the route and have
 * to be changed on both sides at once — `CredentialsResponse` here is one half,
 * the interface of the same name in
 * `plugins/waadoo-core-backend/src/plugins/credentials/plugin.ts` is the
 * other.
 *
 * Unlike the ArgoCD calls on this page, this one goes through Backstage's own
 * API: `fetchApi` attaches the Backstage token by itself, and the route is
 * authenticated by Backstage rather than by an Authelia id_token forwarded
 * through the proxy. Nothing here needs `autheliaAuthApiRef`.
 *
 * Addressed by project AND namespace, like the mails route: the backend
 * decides from the project who may read the credentials, then checks that the
 * namespace belongs to it. A refusal is a 404, which this client reports as
 * any other failure — the page does not ask when it already knows the answer
 * (`credentialsAccess` below).
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { branchKind } from './branchKind';
import type { ProjectAccessLevel } from './projectAccess';
import { safeExternalUrl } from './safeUrl';

/**
 * What the page knows of the caller's right to the credentials, when it knows
 * it in advance (UX only, the route keeps its own rule): `pending` — the
 * caller's level is still being read, nothing is asked meanwhile —;
 * `editors-only` — a plain member of the project; `admins-only` — an editor
 * on the production. Undefined: the card asks, and the route decides.
 */
export type CredentialsAccess = 'pending' | 'editors-only' | 'admins-only';

/**
 * The rule of the `waadoo-credentials` route since 2026-10-29, as the page
 * can tell it beforehand: the admin tier everywhere, the project's editors on
 * every branch but the production (`branchKind`, the same test as the
 * wake/sleep and promotion rights), nobody else. `answered` is false while
 * the caller's level loads (`pending`). A level the page could not read — the
 * access request failed, or the caller is in no group of the project — is
 * left to the route.
 */
export function credentialsAccess(
  answered: boolean,
  level: ProjectAccessLevel | undefined,
  branch: string,
): CredentialsAccess | undefined {
  if (!answered) {
    return 'pending';
  }
  if (level === 'read') {
    return 'editors-only';
  }
  if (level === 'edit' && branchKind(branch) === 'production') {
    return 'admins-only';
  }
  return undefined;
}

export interface CredentialsResponse {
  /**
   * False when the namespace holds no Secret carrying both a login and a
   * password — an environment whose first initialisation has not run yet,
   * or whose type's Secret does not exist yet.
   */
  configured: boolean;
  /**
   * `null` together with `password` and `secretName` when `configured` is
   * false. Not optional, and set on every answer, for the reason
   * `BackupsResponse.allowed` states: a field present on some reads and absent
   * on others makes the card flicker as it reloads.
   */
  login: string | null;
  password: string | null;
  /** The Kubernetes Secret the two values were read from. */
  secretName: string | null;
}

export interface CredentialsRequestOptions {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  /** The project of the page's route, i.e. its `waadoo.ovh/project` label. */
  project: string;
  /**
   * The environment's namespace, read off its ArgoCD Application
   * (`spec.destination.namespace`) and never rebuilt from a naming convention.
   */
  namespace: string;
}

/**
 * The administrator credentials of one environment. A pure read: nothing here
 * creates anything in the cluster, which is what makes it safe to call on
 * mount.
 */
export async function fetchCredentials(
  options: CredentialsRequestOptions,
): Promise<CredentialsResponse> {
  const { fetchApi, discoveryApi, project, namespace } = options;

  // Absolute backend URL: in development the frontend runs on a different
  // port, so a relative path would hit the frontend and return the index page
  // (HTML) instead of JSON.
  const baseUrl = await discoveryApi.getBaseUrl('waadoo-credentials');
  const url = `${baseUrl}/projects/${encodeURIComponent(
    project,
  )}/namespaces/${encodeURIComponent(namespace)}/credentials`;

  const response = await fetchApi.fetch(url);
  if (!response.ok) {
    throw new Error(`Credentials API: HTTP ${response.status}`);
  }

  return (await response.json()) as CredentialsResponse;
}

/**
 * The sign-in page of an environment: its address (`waadoo.ovh/url` of its
 * Application) with the type's `credentials.loginPath`; undefined without
 * either, or for an address that is not http(s). The path starts with one
 * `/` (the schema refuses `//`), so it replaces the address's path and
 * never its host.
 */
export function loginUrlOf(
  url: string | undefined,
  loginPath: string | undefined,
): string | undefined {
  if (url === undefined || loginPath === undefined) return undefined;
  const base = safeExternalUrl(url);
  return base ? new URL(loginPath, base).href : undefined;
}
